// «Настройки» of a pipeline (R-U32, um/2026-10-30/03 п.5, as in amoCRM): name and colour; stages in order — add, rename, recolour, drag,
// the fields a stage requires seen in its row, the stage's scenarios under it; the finals fixed at the end; the archive, and deleting an
// empty pipeline. A tab of the pipeline's page, not a dialog.

import { useState } from 'react';
import { termLock, uuidv7, type FieldDef, type Id, type TermDef } from '@confdeck/core-shared';
import { useMeta, useSync } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { IconButton, Button, Field, Input, Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger, cx, useToast } from '../ui/primitives.tsx';
import { StageRules } from '../schema/StageRules.tsx';
import { navigate, typeHref } from '../shell/router.ts';
import { Page, PageBlock } from '../ui/Page.tsx';
import { pipelineField, pipelineSectionOf, pipelineType } from './pipeline-section.ts';
import { orderBefore } from './stage-order.ts';
import { StageScenarios } from './StageScenarios.tsx';
import s from './pipelines.module.css';

const COLORS = ['gray', 'blue', 'teal', 'green', 'amber', 'red', 'violet', 'pink'];

export function PipelineSettings({ pipelineId }: { pipelineId: Id }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const p = meta.term(pipelineId)!;
  const typeId = pipelineType(meta, pipelineId);
  const type = meta.type(typeId);
  const def = pipelineSectionOf(type?.key);
  const field = typeId ? pipelineField(meta, typeId) : undefined;
  const word = (def?.words.pipelineAcc ?? 'воронку');
  const [name, setName] = useState(p.name);
  const [adding, setAdding] = useState('');
  const [drag, setDrag] = useState<Id | null>(null);
  const [over, setOver] = useState<Id | null>(null);
  const [rules, setRules] = useState<Id | null>(null);
  const stages = meta.children(pipelineId, p.taxonomyId).filter((t) => !t.archivedAt);
  const open = stages.filter((t) => !t.final);
  const finals = stages.filter((t) => t.final);

  const run = async (command: string, input: unknown, ok?: string) => {
    const r = await client.run(command, input);
    if (!r.ok) toast({ kind: 'error', text: r.error.message }); else if (ok) toast({ kind: 'info', text: ok });
    return r.ok;
  };
  const rename = (t: TermDef, v: string) => { if (v.trim() && v.trim() !== t.name) void run('term.update', { id: t.id, name: v.trim() }); };
  /** Puts the dragged stage before `target` (or last among open stages): the order is the middle of its new neighbours. */
  const move = (id: Id, target: Id | null) => { void run('term.update', { id, order: orderBefore(open, finals, id, target) }); };
  const add = async () => {
    const v = adding.trim();
    if (!v) return;
    setAdding('');
    await run('term.create', { id: uuidv7(), taxonomyId: p.taxonomyId, parentId: pipelineId, name: v });
  };
  const away = () => navigate(type ? typeHref(type.key) : '/');
  const archivePipeline = async () => {
    if (!confirm(`Убрать ${word} «${p.name}» в архив? Сначала перенесите её записи. Вернуть можно в «Настройки → Структура».`)) return;
    if (await run('term.archive', { id: pipelineId }, `«${p.name}» в архиве`)) away();
  };
  const deletePipeline = async () => {
    if (!confirm(`Удалить ${word} «${p.name}» со всеми этапами и их сценариями? Удалить можно только пустую — без записей, и в архиве тоже.`)) return;
    if (await run('pipeline.delete', { id: pipelineId }, `«${p.name}» удалена`)) away();
  };
  /** The fields a stage requires (its «Условия перехода», R-C14), named in its row. */
  const required = (t: TermDef) => [...(t.rules?.fields ?? []).map((fid) => meta.get<FieldDef>(fid, 'field')?.name).filter((n): n is string => !!n), ...(t.rules?.reason ? ['причина'] : [])];

  const row = (t: TermDef) => {
    const lock = termLock(meta, t);
    const movable = !t.final;
    const need = required(t);
    return (
      <div key={t.id} className={s.stageBlock}>
        <div className={cx(s.stage, t.final && s.final, drag === t.id && s.dragging, over === t.id && drag !== t.id && s.over)} draggable={movable}
          onDragStart={(e) => { setDrag(t.id); e.dataTransfer.effectAllowed = 'move'; }} onDragEnd={() => { setDrag(null); setOver(null); }}
          onDragOver={(e) => { if (drag && movable) { e.preventDefault(); setOver(t.id); } }}
          onDrop={(e) => { e.preventDefault(); if (drag && drag !== t.id && movable) move(drag, t.id); setDrag(null); setOver(null); }}>
          {movable ? <Icon name="grip" size={13} className={s.grip} /> : <Icon name="lock" size={13} className={s.grip} aria-label="Итоговый этап — всегда в конце" />}
          <Menu>
            <MenuTrigger asChild><button type="button" className={s.swatch} style={{ background: `var(--c-${t.color}-dot)` }} aria-label={`Цвет этапа ${t.name}`} /></MenuTrigger>
            <MenuContent><div className={s.colors}>{COLORS.map((c) => <button key={c} type="button" className={s.swatch} style={{ background: `var(--c-${c}-dot)` }} aria-label={c} onClick={() => void run('term.update', { id: t.id, color: c })} />)}</div></MenuContent>
          </Menu>
          <input className={s.name} defaultValue={t.name} aria-label="Название этапа" onBlur={(e) => rename(t, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { (e.target as HTMLInputElement).value = t.name; (e.target as HTMLInputElement).blur(); } }} />
          {need.length > 0 && <button type="button" className={s.required} onClick={() => setRules(t.id)} title="Условия перехода">Обязательно: {need.join(', ')}</button>}
          {t.rules?.lock && <span className={s.badge}>правка закрыта</span>}
          {t.final && <span className={s.badge}>{t.final === 'won' ? 'успех' : 'отказ'}</span>}
          <Menu>
            <MenuTrigger asChild><IconButton size="sm" variant="ghost" icon="more" label={`Действия с этапом ${t.name}`} /></MenuTrigger>
            <MenuContent align="end">
              <MenuItem icon="filter" onSelect={() => setRules(t.id)}>Условия перехода…</MenuItem>
              <MenuSeparator />
              {lock.archive ? <MenuItem icon="archive" danger onSelect={() => void run('term.archive', { id: t.id }, `Этап «${t.name}» в архиве`)}>В архив</MenuItem> : <MenuItem icon="lock" disabled>{lock.why}</MenuItem>}
            </MenuContent>
          </Menu>
        </div>
        {typeId && field && <StageScenarios stage={t} typeId={typeId} field={field} def={def} />}
      </div>
    );
  };

  // The schema is the owner's (term commands check it on the server): others see the stages, without fields, drag or menus.
  if (meta.user(client.header?.userId)?.role !== 'owner') return (
    <Page>
      <PageBlock title="Этапы" help="Этапы, их порядок, условия и сценарии меняет владелец аккаунта.">
        <div className={s.stages}>
          {stages.map((t) => (
            <div key={t.id} className={cx(s.stage, t.final && s.final)}>
              <span className={s.swatch} style={{ background: `var(--c-${t.color}-dot)`, cursor: 'default' }} /><span className={s.nameStatic}>{t.name}</span>
              {required(t).length > 0 && <span className={s.badge}>обязательно: {required(t).join(', ')}</span>}
              {t.final && <span className={s.badge}>{t.final === 'won' ? 'успех' : 'отказ'}</span>}
            </div>
          ))}
        </div>
      </PageBlock>
    </Page>
  );
  return (
    <Page>
      <PageBlock title="Основное">
        <div className={s.top}>
          <Field label="Название"><Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => rename(p, name)} onKeyDown={(e) => e.key === 'Enter' && rename(p, name)} /></Field>
          <Menu>
            <MenuTrigger asChild><Button variant="secondary"><span className={s.swatch} style={{ background: `var(--c-${p.color}-dot)` }} />Цвет</Button></MenuTrigger>
            <MenuContent align="end"><div className={s.colors}>{COLORS.map((c) => <button key={c} type="button" className={s.swatch} style={{ background: `var(--c-${c}-dot)` }} aria-label={c} onClick={() => void run('term.update', { id: p.id, color: c })} />)}</div></MenuContent>
          </Menu>
        </div>
      </PageBlock>
      <PageBlock title="Этапы и сценарии" help="Этапы идут в этом порядке на доске; перетащите этап, чтобы поменять порядок. Итоговые «успех» и «отказ» всегда в конце. Под этапом — его сценарии: они срабатывают, когда запись переходит на этап.">
        <div className={s.stages} onDragOver={(e) => { if (drag) e.preventDefault(); }} onDrop={(e) => { if (drag && e.target === e.currentTarget) { move(drag, null); setDrag(null); setOver(null); } }}>
          {open.map(row)}
          <Input value={adding} placeholder="+ Новый этап — название и Enter" aria-label="Новый этап" onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void add(); }} />
          {finals.map(row)}
        </div>
      </PageBlock>
      <PageBlock title="Архив и удаление" tone="danger" help={`В архив — когда записи нужно сохранить; вернуть можно в «Настройки → Структура». Удалить можно только пустую: без записей, и в архиве тоже.`}>
        <div className={s.dangerRow}>
          <Button variant="ghost" icon="archive" onClick={() => void archivePipeline()}>Убрать в архив</Button>
          <Button variant="ghost" icon="trash" onClick={() => void deletePipeline()}>Удалить</Button>
        </div>
      </PageBlock>
      {rules && <StageRules termId={rules} onClose={() => setRules(null)} />}
    </Page>
  );
}
